import {
  ArrowRight,
  BarChart3,
  Coins,
  Inbox,
  Loader2,
  MessageSquare,
  Send,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useAuth } from "@/auth/useAuth";
import { BarChartVertical, BarList } from "@/components/charts";
import { PageHeader } from "@/components/layout/PageHeader";
import { Badge } from "@/components/ui/badge";
import { SectionCard } from "@/components/ui/section-card";
import { StatCard } from "@/components/ui/stat-card";
import {
  usePendingInstallRequests,
  usePendingPublishRequests,
} from "@/features/agents/hooks/useAgentInstall";
import { useAgents } from "@/features/agents/hooks/useAgents";
import { useHitlRequests } from "@/features/hitl/hooks/useHitl";
import { useMyInstalledAgents } from "@/features/marketplace/hooks/useMarketplace";
import { useMeUsage } from "@/features/observability/hooks/useObservability";
import { useTeamSessions } from "@/features/team-sessions/hooks/useTeamSessions";
import { ThreadListItem } from "@/features/threads/components/ThreadListItem";
import { useThreads } from "@/features/threads/hooks/useThreads";
import { formatCostUsd } from "@/lib/cost";

export function HomePage() {
  const { t } = useTranslation("home");
  const { user, isAdmin, memberships } = useAuth();
  const firstName = user?.name?.split(" ")[0] ?? "";

  const hasManager = memberships.some((m) => m.role === "manager");
  const canApprove = isAdmin || hasManager;

  const hitl = useHitlRequests();
  const installs = usePendingInstallRequests();
  const publishes = usePendingPublishRequests();
  const threads = useThreads();
  const usage = useMeUsage();
  const agentsQuery = useAgents();
  const installedAgentsQuery = useMyInstalledAgents();
  const sessionsQuery = useTeamSessions();

  const agentNameById = useMemo(() => {
    const map = new Map<string, string>();
    agentsQuery.data?.forEach((a) => map.set(a.id, a.name));
    installedAgentsQuery.data?.forEach((a) => map.set(a.id, a.name));
    return map;
  }, [agentsQuery.data, installedAgentsQuery.data]);
  const sessionNameById = useMemo(() => {
    const map = new Map<string, string>();
    sessionsQuery.data?.forEach((s) => map.set(s.id, s.name));
    return map;
  }, [sessionsQuery.data]);

  const hitlCount = canApprove ? hitl.data?.length ?? 0 : 0;
  const installCount = canApprove ? installs.data?.length ?? 0 : 0;
  const publishCount = canApprove ? publishes.data?.length ?? 0 : 0;
  const totalApprovals = hitlCount + installCount + publishCount;

  const recentThreads = (threads.data ?? []).filter((th) => !th.is_sandbox);

  const costByDayData = useMemo(
    () =>
      (usage.data?.cost_by_day ?? []).map((d) => ({
        label: d.date.slice(5), // MM-DD
        value: d.cost,
      })),
    [usage.data?.cost_by_day],
  );
  const costByAgentData = useMemo(
    () =>
      (usage.data?.cost_by_agent ?? []).slice(0, 5).map((d) => ({
        label: d.agent_name,
        value: d.cost,
      })),
    [usage.data?.cost_by_agent],
  );

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      <PageHeader
        title={t("title", { name: firstName })}
        description={t("subtitle")}
      />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label={t("kpi.totalRequests")}
          value={usage.data?.total_requests.toLocaleString("pt-BR") ?? "—"}
          icon={Send}
          loading={usage.isLoading}
        />
        <StatCard
          label={t("kpi.totalCost")}
          value={
            usage.data ? formatCostUsd(usage.data.total_cost) : "—"
          }
          icon={Coins}
          loading={usage.isLoading}
        />
        <StatCard
          label={t("kpi.activeThreads")}
          value={usage.data?.active_threads.toLocaleString("pt-BR") ?? "—"}
          icon={MessageSquare}
          loading={usage.isLoading}
        />
        <StatCard
          label={t("kpi.totalTokens")}
          value={usage.data?.total_tokens.toLocaleString("pt-BR") ?? "—"}
          icon={Sparkles}
          loading={usage.isLoading}
        />
      </div>

      <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-3">
        {/* Coluna esquerda: conversas recentes (cresce, scroll interno).
            ``min-w-0`` impede que titulos longos / conteudo largo
            estourem a coluna do grid (grid items tem min-width:auto
            por default). */}
        <SectionCard
          className="lg:col-span-2 min-h-0 min-w-0"
          title={
            <span className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-brand-500" />{" "}
              {t("sections.recentConversations")}
            </span>
          }
          description={t("sections.recentConversationsHint")}
          actions={
            <Link
              to="/threads"
              className="inline-flex items-center gap-1 text-xs font-semibold text-brand-700 hover:underline"
            >
              {t("actions.viewAll")}
              <ArrowRight className="h-3 w-3" />
            </Link>
          }
        >
          {threads.isLoading ? (
            <LoadingInline />
          ) : recentThreads.length === 0 ? (
            <EmptyInline
              icon={<Inbox className="h-6 w-6" />}
              label={t("empty.conversations")}
            />
          ) : (
            <ul className="scrollbar-clean flex max-h-full flex-col gap-2 overflow-y-auto pr-1">
              {recentThreads.map((thread) => (
                <ThreadListItem
                  key={thread.id}
                  thread={thread}
                  agentName={
                    thread.agent_id
                      ? agentNameById.get(thread.agent_id) ?? null
                      : null
                  }
                  teamSessionName={
                    thread.team_session_id
                      ? sessionNameById.get(thread.team_session_id) ?? null
                      : null
                  }
                />
              ))}
            </ul>
          )}
        </SectionCard>

        {/* Coluna direita: stack Aprovações + Charts de Uso. */}
        <div className="flex min-h-0 min-w-0 flex-col gap-4">
          {canApprove ? (
            <SectionCard
              title={
                <span className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-brand-500" />{" "}
                  {t("sections.pendingApprovals")}
                </span>
              }
              actions={
                <Link
                  to="/me/pending-approvals"
                  className="inline-flex items-center gap-1 text-xs font-semibold text-brand-700 hover:underline"
                >
                  {t("actions.viewAll")}
                  <ArrowRight className="h-3 w-3" />
                </Link>
              }
            >
              {totalApprovals === 0 ? (
                <EmptyInline
                  icon={<ShieldCheck className="h-6 w-6" />}
                  label={t("empty.approvals")}
                />
              ) : (
                <ul className="flex flex-col gap-2">
                  <ApprovalRow
                    label="HITL"
                    count={hitlCount}
                    loading={hitl.isLoading}
                  />
                  <ApprovalRow
                    label={t("sections.installs")}
                    count={installCount}
                    loading={installs.isLoading}
                  />
                  <ApprovalRow
                    label={t("sections.publishes")}
                    count={publishCount}
                    loading={publishes.isLoading}
                  />
                </ul>
              )}
            </SectionCard>
          ) : null}

          <SectionCard
            className="flex-1 min-h-0"
            title={
              <span className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-brand-500" />{" "}
                {t("sections.usage")}
              </span>
            }
            actions={
              <Link
                to="/observability/me"
                className="inline-flex items-center gap-1 text-xs font-semibold text-brand-700 hover:underline"
              >
                {t("actions.viewDetails")}
                <ArrowRight className="h-3 w-3" />
              </Link>
            }
          >
            {usage.isLoading ? (
              <LoadingInline />
            ) : !usage.data ? (
              <EmptyInline
                icon={<BarChart3 className="h-6 w-6" />}
                label={t("empty.usage")}
              />
            ) : (
              <div className="flex min-w-0 flex-col gap-4">
                <div className="min-w-0">
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-500">
                    {t("charts.costByDay")}
                  </p>
                  {costByDayData.length === 0 ? (
                    <p className="py-4 text-center text-xs text-ink-500">
                      {t("empty.usage")}
                    </p>
                  ) : (
                    <div className="h-[140px] w-full">
                      <BarChartVertical
                        data={costByDayData}
                        valueFormatter={formatCostUsd}
                      />
                    </div>
                  )}
                </div>
                <div className="min-w-0">
                  <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-500">
                    {t("charts.costByAgent")}
                  </p>
                  {costByAgentData.length === 0 ? (
                    <p className="py-4 text-center text-xs text-ink-500">
                      {t("empty.usage")}
                    </p>
                  ) : (
                    <BarList
                      data={costByAgentData}
                      valueFormatter={formatCostUsd}
                    />
                  )}
                </div>
              </div>
            )}
          </SectionCard>
        </div>
      </div>
    </div>
  );
}

interface ApprovalRowProps {
  label: string;
  count: number;
  loading?: boolean;
}

function ApprovalRow({ label, count, loading }: ApprovalRowProps) {
  return (
    <li className="flex items-center justify-between rounded-xl border border-line bg-canvas px-3 py-2">
      <span className="text-sm font-medium text-ink-900">{label}</span>
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin text-ink-400" />
      ) : count > 0 ? (
        <Badge>{count}</Badge>
      ) : (
        <Badge variant="secondary">0</Badge>
      )}
    </li>
  );
}

function EmptyInline({
  icon,
  label,
}: {
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <div className="flex flex-col items-center gap-2 py-6 text-center text-ink-500">
      <span className="text-ink-400">{icon}</span>
      <span className="text-sm">{label}</span>
    </div>
  );
}

function LoadingInline() {
  return (
    <div className="flex items-center justify-center py-6 text-ink-500">
      <Loader2 className="h-5 w-5 animate-spin text-brand-500" />
    </div>
  );
}
